import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { AppDispatch } from "store/store";
import { refetchParams, UseGenericMultiSelectProps } from "./types";
import { GenericMultiSelect } from "./";

export function useGenericMultiSelect<T, Y>({
    items,
    previouslySelectedIds,
    onSubmit,
    onChange,
    showNone = false,
    allowUnselect = false,
    isMultiple,
    allowItemClick = true,
    checkBoxLocation = "right",
    actionsBarOptions,
    searchSortOptions,
    allowNoSelection = false,
    loading = false,
    emptyStateCopy,
    supressNotifications = false,
    showSelectedCountOnActions,
    getNextPage,
    nextPageLoading,
    scrollable = false,
    fetchWithNewParams
}: UseGenericMultiSelectProps<T, Y>) {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();

    const [passthroughError, setPassthroughError] = useState<string>();

    // An ordered list of ids for items to be displayed
    const [displayOrder, setDisplayOrder] = useState<string[]>(
        items?.map((i) => i.id)
    );

    // Manage the which items are selected and which are visible
    const [selectedIdSet, setSelectedIdSet] = useState<Set<string>>(
        new Set(previouslySelectedIds)
    );

    const prevItemsCountRef = useRef<number | undefined>(items?.length);

    useEffect(() => {
        const prevSelectedSet = new Set(previouslySelectedIds);

        if (previouslySelectedIds) {
            setSelectedIdSet(prevSelectedSet);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previouslySelectedIds]);

    // Keep selection in sync with the current items list:
    // - prune ids that no longer exist
    // - if items transition from non-empty -> empty (e.g. "delete all"), clear selection
    //   (but do NOT clear just because items is empty on initial load / transient states).
    useEffect(() => {
        if (!items) return;

        const prevCount = prevItemsCountRef.current;
        prevItemsCountRef.current = items.length;

        if (items.length === 0) {
            if (typeof prevCount === "number" && prevCount > 0) {
                setSelectedIdSet(new Set());
                onChange?.([]);
            }
            return;
        }

        setSelectedIdSet((prev) => {
            const validIds = new Set(items.map((i) => i.id));
            const next = new Set<string>();

            prev.forEach((id) => {
                if (validIds.has(id)) next.add(id);
            });

            if (next.size !== prev.size) {
                onChange?.(Array.from(next));
            }

            return next.size === prev.size ? prev : next;
        });
    }, [items, onChange]);

    const { visibleItems, anySelected, anyNewSelected, anyVisibleSelected } =
        useMemo(() => {
            if (!items || items?.length === 0) {
                return {
                    visibleItems: null,
                    anySelected: false,
                    anyNewSelected: false,
                    anyVisibleSelected: false
                };
            }

            const orderedItems =
                // If no display order
                !displayOrder ||
                // Or the display order is empty and search sort is not shown
                (displayOrder.length === 0 &&
                    !searchSortOptions?.showSearchSort)
                    ? // Show all items
                      items
                    : // Otherwise, show only the items in the displayOrder
                      displayOrder.map((id) => {
                          return items.find((i) => i.id === id);
                      });

            // If we should hide previously selected items, filter them out
            const filterPreviouslySelected =
                !allowUnselect && previouslySelectedIds?.length > 0;
            const visibleItems = filterPreviouslySelected
                ? orderedItems.filter(
                      (i) => i && !previouslySelectedIds?.includes(i.id)
                  )
                : orderedItems;

            // Return visible items and related visibilty/selection information
            return {
                visibleItems,
                anySelected: selectedIdSet.size > 0,
                anyNewSelected: items.some(
                    (i) =>
                        selectedIdSet.has(i.id) &&
                        !previouslySelectedIds?.includes(i.id)
                ),
                anyVisibleSelected: visibleItems?.some((i) =>
                    selectedIdSet.has(i?.id)
                )
            };
        }, [
            items,
            displayOrder,
            selectedIdSet,
            previouslySelectedIds,
            allowUnselect,
            searchSortOptions
        ]);

    const handleSelectedIdToggle = useCallback(
        (id: string) => {
            let clonedSet = new Set(selectedIdSet);

            if (clonedSet.has(id)) {
                clonedSet.delete(id);
            } else {
                if (!isMultiple) {
                    clonedSet.clear();
                }
                clonedSet.add(id);
            }

            setSelectedIdSet(clonedSet);
            onChange?.(Array.from(clonedSet));
        },
        [selectedIdSet, isMultiple, onChange]
    );

    const handleSelect = useCallback(
        (id: string) => {
            handleSelectedIdToggle(id);
        },
        [handleSelectedIdToggle]
    );

    const handleSelectOrDeselectAll = useCallback(() => {
        let newSelectedItems: string[] = [];
        if (
            // If none are selected
            selectedIdSet.size === 0 ||
            ([...selectedIdSet].filter(
                // Or all the selected items were previously selected
                (id) => !previouslySelectedIds?.includes(id)
            ).length === 0 &&
                !allowUnselect) ||
            // Or none of the visible items are selected
            visibleItems.filter((i) => selectedIdSet.has(i.id)).length === 0
        ) {
            // Add all visible item ids to the selected set, reversing the order
            const newSelectedVisibleItems = items
                .map((i) => i.id)
                .filter((i) => {
                    return visibleItems.map((i) => i.id).includes(i);
                })
                .reverse();
            newSelectedItems = [...selectedIdSet, ...newSelectedVisibleItems];
        } else if (allowUnselect || previouslySelectedIds?.length === 0) {
            // If unselection is allowed or there are no previously selected items, unselect all
            newSelectedItems = [];
        } else {
            // If unselection is not allowed, unselect all besides previously selected
            newSelectedItems = items
                .filter((i) => previouslySelectedIds?.includes(i.id))
                .map((i) => {
                    return i.id;
                });
        }

        setSelectedIdSet(new Set(newSelectedItems));
    }, [
        selectedIdSet,
        allowUnselect,
        visibleItems,
        previouslySelectedIds,
        items
    ]);

    const handleSubmit = useCallback(() => {
        if (!allowNoSelection && !anySelected) {
            if (supressNotifications) {
                setPassthroughError(t("errors:no-items-selected"));
            } else {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: t("errors:no-items-selected")
                    })
                );
            }
            return;
        }
        onSubmit([...selectedIdSet]);
    }, [
        allowNoSelection,
        onSubmit,
        dispatch,
        t,
        selectedIdSet,
        anySelected,
        supressNotifications
    ]);

    const searchSortOptionsFinal = useMemo(() => {
        const newOptions = { ...searchSortOptions };

        if (fetchWithNewParams) {
            newOptions.refetchWithNewParams = (
                newParams: refetchParams,
                fromDebouncedTextInputEffect = false
            ) => {
                const shouldReset = fetchWithNewParams(
                    newParams,
                    false,
                    fromDebouncedTextInputEffect
                );
                if (!shouldReset) return;

                const prevSelectedSet = new Set(previouslySelectedIds);
                setSelectedIdSet(prevSelectedSet);
            };
        }
        return newOptions;
    }, [searchSortOptions, fetchWithNewParams, previouslySelectedIds]);

    const GenericMultiSelectComponent = useMemo(() => {
        return (
            <GenericMultiSelect<T, Y>
                items={items}
                selectedIdSet={selectedIdSet}
                previouslySelectedIds={previouslySelectedIds}
                handleSelect={handleSelect}
                handleSelectAll={handleSelectOrDeselectAll}
                allowUnselect={allowUnselect}
                allowItemClick={allowItemClick}
                isMultiple={isMultiple}
                showNone={showNone}
                checkBoxLocation={checkBoxLocation}
                actionsBarOptions={actionsBarOptions}
                searchSortOptions={searchSortOptionsFinal}
                loading={loading}
                displayOrder={displayOrder}
                setDisplayOrder={setDisplayOrder}
                visibleItems={visibleItems}
                emptyStateCopy={emptyStateCopy}
                showSelectedCountOnActions={showSelectedCountOnActions}
                getNextPage={getNextPage}
                nextPageLoading={nextPageLoading}
                scrollable={scrollable}
            />
        );
    }, [
        handleSelect,
        handleSelectOrDeselectAll,
        allowUnselect,
        isMultiple,
        showNone,
        actionsBarOptions,
        searchSortOptionsFinal,
        checkBoxLocation,
        allowItemClick,
        loading,
        items,
        selectedIdSet,
        previouslySelectedIds,
        displayOrder,
        setDisplayOrder,
        visibleItems,
        emptyStateCopy,
        showSelectedCountOnActions,
        getNextPage,
        nextPageLoading,
        scrollable
    ]);
    return {
        GenericMultiSelectComponent,
        handleSelectOrDeselectAll,
        anySelected,
        handleSubmit,
        anyNewSelected,
        visibleItems,
        anyVisibleSelected,
        selectedIdSet,
        passthroughError,
        setPassthroughError
    };
}
